import { useCallback, useEffect } from 'react'
import { Classroom as ClassroomConstants } from '@constants'
import { useForceUpdate } from '@hooks'
import { ClassroomManager } from '@helpers'

import { initialPlaybackState, PlaybackStateObserver } from '../helpers'
import {
  useRecordingActions,
  useRecordingPlaybackDispatch,
  useRecordingPlaybackStateObserver
} from './RecordingProvider'
import { useRecordingFileUpdates } from './useRecordingData'

export const useRecordingBootstrap = ({
  classroomId,
  updates,
  media,
  profile,
  env
}) => {
  const forceUpdate = useForceUpdate()
  const playbackStateObserverRef = useRecordingPlaybackStateObserver()
  const setPlayback = useRecordingPlaybackDispatch()
  const { onPanelsChange, onParticipantsChange, setStep, reset } =
    useRecordingActions()
  const { mutateAsync: getFileUpdates } = useRecordingFileUpdates()

  const fetchFileUpdates = useCallback(
    function _fetchFileUpdates(events) {
      if (!events) return
      const [event] = events
      if (!event?.keysChanged?.has('active')) return

      const fileId = event.target.get('active')
      if (!fileId) return

      playbackStateObserverRef.current.loadFileUpdates(fileId)
    },
    [playbackStateObserverRef]
  )

  const onDeviceChange = useCallback(() => {
    playbackStateObserverRef.current.pause()
  }, [playbackStateObserverRef])

  const onMediaSessionActionPlay = useCallback(() => {
    if (playbackStateObserverRef.current.getState().loading) return
    playbackStateObserverRef.current.play()
  }, [playbackStateObserverRef])

  const onMediaSessionActionPause = useCallback(() => {
    playbackStateObserverRef.current.pause()
  }, [playbackStateObserverRef])

  useEffect(() => {
    const stateObserver = new PlaybackStateObserver({ media, getFileUpdates })
    window.so = stateObserver // TODO :: for debug purposes
    playbackStateObserverRef.current = stateObserver

    ClassroomManager.init({ id: classroomId, user: profile, env })
    ClassroomManager.observe('panels', onPanelsChange)
    ClassroomManager.observe('participants', onParticipantsChange)
    ClassroomManager.observe('frames', fetchFileUpdates)

    stateObserver.on('state:update', (state) => {
      setPlayback(state)
    })

    stateObserver.on('doc:update', (updates) => {
      ClassroomManager.applyUpdates(updates)
    })

    stateObserver.on('cleanup', () => {
      forceUpdate()
    })

    stateObserver.init(updates)
    setPlayback(stateObserver.getState())

    navigator.mediaDevices?.addEventListener('devicechange', onDeviceChange)
    /**
     * @ref https://developers.google.com/web/updates/2017/02/media-session
     */
    navigator.mediaSession?.setActionHandler('play', onMediaSessionActionPlay)
    navigator.mediaSession?.setActionHandler('pause', onMediaSessionActionPause)
    setStep(ClassroomConstants.STEPS.READY)

    return () => {
      navigator.mediaDevices?.removeEventListener(
        'devicechange',
        onDeviceChange
      )
      navigator.mediaSession?.setActionHandler('play', null)
      navigator.mediaSession?.setActionHandler('pause', null)

      stateObserver.destroy()
      playbackStateObserverRef.current = null
      reset()
      setPlayback(initialPlaybackState)

      ClassroomManager.unobserve('panels', onPanelsChange)
      ClassroomManager.unobserve('participants', onParticipantsChange)
      ClassroomManager.unobserve('frames', fetchFileUpdates)
      ClassroomManager.destroy()
    }
  }, [
    env,
    fetchFileUpdates,
    onPanelsChange,
    onParticipantsChange,
    playbackStateObserverRef,
    classroomId,
    updates,
    setPlayback,
    setStep,
    profile,
    reset,
    forceUpdate,
    onDeviceChange,
    onMediaSessionActionPlay,
    onMediaSessionActionPause,
    media,
    getFileUpdates
  ])
}
